import {
  clampViewportScrollTop,
  scrollMessengerTailIntoView,
  viewportMaxScrollTop,
} from '@/components/Messenger/shared/viewport/messengerViewportScroll';

/** Селектор корневого слота сообщения в DM-ленте (совпадает с useThreadReadTracking / DirectThreadView). */
export function dmMsgQuerySelector(viewport: HTMLElement, messageId: string): Element | null {
  const sel =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? `[data-dm-msg="${CSS.escape(messageId)}"]`
      : `[data-dm-msg="${messageId.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;
  return viewport.querySelector(sel);
}

export type DmScrollAnchor = {
  anchorMessageId: string;
  /** Смещение верхнего края слота сообщения от верха viewport (px). */
  offsetFromViewportTop: number;
};

/**
 * Первое сообщение, пересекающееся с viewport по вертикали; иначе первое найденное в DOM.
 * Порядок id — сверху вниз (как в ленте).
 */
export function computeDmViewportScrollAnchor(
  viewport: HTMLElement,
  messageIdsOrdered: string[]
): DmScrollAnchor | null {
  const vr = viewport.getBoundingClientRect();
  for (const id of messageIdsOrdered) {
    const el = dmMsgQuerySelector(viewport, id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.bottom > vr.top && r.top < vr.bottom) {
      return { anchorMessageId: id, offsetFromViewportTop: r.top - vr.top };
    }
  }
  for (const id of messageIdsOrdered) {
    const el = dmMsgQuerySelector(viewport, id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    return { anchorMessageId: id, offsetFromViewportTop: r.top - vr.top };
  }
  return null;
}

/**
 * Восстановить вертикальное положение относительно якоря (как при «Загрузить ещё»).
 * @returns false, если элемент с id не найден в viewport.
 */
export function applyDmViewportScrollAnchor(viewport: HTMLElement, anchor: DmScrollAnchor): boolean {
  const el = dmMsgQuerySelector(viewport, anchor.anchorMessageId);
  if (!el) return false;
  const vr = viewport.getBoundingClientRect();
  const ar = el.getBoundingClientRect();
  viewport.scrollTop += ar.top - vr.top - anchor.offsetFromViewportTop;
  return true;
}

export type DmViewportResumeOpts = {
  anchor: DmScrollAnchor | null | undefined;
  /** Сохранённый scrollTop на момент ухода — грубая оценка и fallback, если якорь ещё не в DOM. */
  fallbackScrollTop: number | undefined;
  tailEl: Element | null;
};

/**
 * Восстановление позиции при открытии чата / после resize: сначала fallback scrollTop, затем уточнение по якорю.
 */
export function applyDmViewportResumePosition(viewport: HTMLElement, opts: DmViewportResumeOpts): void {
  const max = viewportMaxScrollTop(viewport);
  if (opts.anchor) {
    const guess =
      opts.fallbackScrollTop !== undefined
        ? Math.max(0, Math.min(opts.fallbackScrollTop, max))
        : max;
    viewport.scrollTop = guess;
    if (!applyDmViewportScrollAnchor(viewport, opts.anchor) && opts.fallbackScrollTop !== undefined) {
      viewport.scrollTop = Math.max(0, Math.min(opts.fallbackScrollTop, max));
    }
    clampViewportScrollTop(viewport);
    return;
  }
  if (opts.fallbackScrollTop !== undefined) {
    viewport.scrollTop = Math.max(0, Math.min(opts.fallbackScrollTop, max));
    return;
  }
  scrollMessengerTailIntoView(viewport, opts.tailEl, 'instant');
}
